// ─── AttachmentButton — the 📎 badge on every expense/income row ───────────
// High-contrast design so users immediately see the affordance:
//   • Empty   → dashed accent ring + "+" overlay on a tinted accent surface.
//   • Attached → SOLID accent-filled badge with a gentle glow. Clear that
//     something is there; clicking opens the actions menu.
//   • Uploading → spinner, same footprint.
// Hover tooltip (~150 ms delay) always shows:
//   • empty state  → "Attach invoice / receipt — PDF, JPG, PNG, up to 5 MB"
//   • attached     → file name + size, who uploaded, when.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS } from "../lib/theme.js";
import { useAttachments, entityKey, ALLOWED_MIME } from "../hooks/useAttachments.js";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  if (ms < 7 * 86_400_000) return `${Math.round(ms / 86_400_000)}d ago`;
  return d.toLocaleString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
}

const HOVER_DELAY_MS = 150;

// `fallback` → if the primary (entityType, entityId) has no attachment yet,
// show the attachment that belongs to this other entity (e.g. an order-
// expense row with no direct PDF falls back to the parent order's invoice).
// The fallback attachment is read-only via this button: View / Replace /
// Remove are disabled on it to avoid editing the parent from a child row.
export default function AttachmentButton({
  entityType, entityId, brandKey = "main", disabled = false, size = 18,
  fallback = null, // { entityType, entityId } | null
  onUpgrade, // optional: () => void to open paywall on locked-state click
}) {
  const T = useTheme();
  const { byEntity, upload, openInNewTab, remove, canAttach } = useAttachments({ brandKey });
  const [menuOpen, setMenuOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [tipOpen, setTipOpen] = useState(false);
  const hoverTimer = useRef(null);
  const fileInputRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const directAttachment = byEntity.get(entityKey(entityType, entityId)) || null;
  const fallbackAttachment = !directAttachment && fallback
    ? (byEntity.get(entityKey(fallback.entityType, fallback.entityId)) || null)
    : null;
  const attachment = directAttachment || fallbackAttachment;
  const hasFile = !!attachment;
  const isFallback = !directAttachment && !!fallbackAttachment;

  // Anchor rect for portal positioning — recomputed when menu/tooltip opens
  // or the viewport changes. Because the menu is portalled to <body>, it
  // escapes any `overflow:hidden` ancestors (eg the collapsed Recurring Card)
  // that would otherwise clip it.
  const [anchorRect, setAnchorRect] = useState(null);
  const recomputeAnchor = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    setAnchorRect(el.getBoundingClientRect());
  }, []);
  useLayoutEffect(() => {
    if (!menuOpen && !tipOpen) return;
    recomputeAnchor();
    function onScroll() { setMenuOpen(false); setTipOpen(false); }
    window.addEventListener("resize", recomputeAnchor);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", recomputeAnchor);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menuOpen, tipOpen, recomputeAnchor]);

  // Close menu on outside click / Esc
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e) {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setMenuOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); }, []);

  function onEnter() {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setTipOpen(true), HOVER_DELAY_MS);
  }
  function onLeave() {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setTipOpen(false);
  }

  function handleClick(e) {
    e.stopPropagation();
    if (disabled || uploading) return;
    setTipOpen(false);
    if (hasFile) {
      // Even on Starter, an existing attachment can still be opened —
      // the gate is only on NEW uploads. The menu handles view/replace/remove;
      // replace will be blocked by the server on Starter, which surfaces as
      // the error toast.
      setMenuOpen(o => !o);
    } else if (!canAttach) {
      // Locked → invoke upgrade callback if the host page passed one,
      // otherwise show a simple upgrade toast.
      if (typeof onUpgrade === "function") { onUpgrade(); return; }
      setError("Upgrade to Pro to attach invoices and receipts.");
      setTimeout(() => setError(""), 3500);
    } else {
      fileInputRef.current?.click();
    }
  }

  async function onFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(""); setUploading(true); setMenuOpen(false);
    try {
      await upload({ file, entityType, entityId });
    } catch (err) {
      setError(err?.message || "Upload failed");
      setTimeout(() => setError(""), 4000);
    } finally {
      setUploading(false);
    }
  }

  async function onView() {
    setMenuOpen(false);
    try { await openInNewTab(attachment.id); }
    catch (err) { setError(err?.message || "Could not open"); setTimeout(() => setError(""), 3000); }
  }
  async function onReplace() {
    setMenuOpen(false);
    fileInputRef.current?.click();
  }
  async function onRemove() {
    setMenuOpen(false);
    if (!confirm(`Remove attachment "${attachment.file_name}"?`)) return;
    try { await remove(attachment.id); }
    catch (err) { setError(err?.message || "Delete failed"); setTimeout(() => setError(""), 3000); }
  }

  // ── Visual states ─────────────────────────────────────────────────────────
  // Keep the footprint a consistent square so rows don't shift.
  const outer = size + 10; // 28 px for size=18

  const btnStyle = uploading
    ? {
        background: T.accent + "20",
        border: `2px solid ${T.accent}66`,
        color: T.accent,
        boxShadow: "none",
      }
    : hasFile
    ? {
        // SOLID attached badge — clear, contrasting, not easy to miss
        background: T.accent,
        border: `2px solid ${T.accent}`,
        color: "#fff",
        boxShadow: `0 2px 8px ${T.accent}66, 0 0 0 3px ${T.accent}22`,
      }
    : !canAttach
    ? {
        // Locked — Starter accounts. Muted look with a lock overlay; click
        // opens the paywall (or an inline "upgrade" toast if no callback).
        background: T.surface,
        border: `2px dashed ${T.border}`,
        color: T.textMuted,
        boxShadow: "none",
      }
    : {
        // Empty slot — dashed accent outline + tinted fill + "+" overlay
        background: T.accent + "14",
        border: `2px dashed ${T.accent}88`,
        color: T.accent,
        boxShadow: "none",
      };

  const tooltip = hasFile
    ? (
      <>
        <div style={{ fontWeight:700, color:T.text, marginBottom:2, maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          📎 {attachment.file_name}
        </div>
        <div style={{ color:T.textSub, fontSize:10 }}>{fmtSize(attachment.file_size)}</div>
        {isFallback && (
          <div style={{ color:T.accent, fontSize:10, marginTop:3, fontWeight:700 }}>
            ↳ Inherited from parent order
          </div>
        )}
        <div style={{ color:T.textSub, fontSize:10, marginTop:4 }}>
          <strong style={{ color:T.text }}>Uploaded by </strong>
          {attachment.uploaded_by_email || "team member"}
        </div>
        <div style={{ color:T.textMuted, fontSize:10, marginTop:1 }}>
          {fmtWhen(attachment.uploaded_at)}
          {attachment.uploaded_at && <span style={{ opacity:0.6, marginLeft:6 }}>
            · {new Date(attachment.uploaded_at).toLocaleString("en-GB",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}
          </span>}
        </div>
      </>
    )
    : !canAttach
    ? (
      <>
        <div style={{ fontWeight:700, color:T.text, marginBottom:2 }}>🔒 Pro feature</div>
        <div style={{ color:T.textSub, fontSize:10 }}>Upgrade to Pro to attach invoices &amp; receipts to any transaction or order.</div>
      </>
    )
    : (
      <>
        <div style={{ fontWeight:700, color:T.text, marginBottom:2 }}>Attach invoice / receipt</div>
        <div style={{ color:T.textSub, fontSize:10 }}>PDF, JPG, PNG · up to 5 MB</div>
      </>
    );

  return (
    <span
      style={{ position:"relative", display:"inline-flex", alignItems:"center" }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <button
        ref={btnRef}
        type="button"
        disabled={disabled || uploading}
        onClick={handleClick}
        aria-label={hasFile ? `Attachment: ${attachment.file_name}` : "Attach invoice or receipt"}
        style={{
          display:"inline-flex", alignItems:"center", justifyContent:"center",
          width:outer, height:outer, borderRadius:RADIUS.md,
          padding:0, lineHeight:1, fontFamily:FONT, position:"relative",
          cursor: disabled ? "not-allowed" : (uploading ? "wait" : "pointer"),
          transition: "all 0.15s ease",
          opacity: disabled ? 0.4 : 1,
          ...btnStyle,
        }}
      >
        {uploading ? (
          <span style={{ fontSize: size - 4, animation:"sfSpin 0.9s linear infinite" }}>⟳</span>
        ) : (
          <>
            <span style={{ fontSize: size - 2, lineHeight:1 }}>📎</span>
            {/* Corner overlay — "+" when uploadable, lock when gated */}
            {!hasFile && (
              <span style={{
                position:"absolute", bottom:-3, right:-3,
                width:12, height:12, borderRadius:"50%",
                background: canAttach ? T.accent : T.textMuted,
                color:"#fff",
                fontSize: canAttach ? 10 : 8, fontWeight: 900, lineHeight:1,
                display:"flex", alignItems:"center", justifyContent:"center",
                boxShadow: `0 2px 4px ${(canAttach ? T.accent : T.textMuted)}88`,
              }}>{canAttach ? "+" : "🔒"}</span>
            )}
          </>
        )}
      </button>
      <style>{`@keyframes sfSpin{to{transform:rotate(360deg)}}`}</style>

      {/* Hidden file input — used by both upload and replace */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_MIME.join(",")}
        onChange={onFilePicked}
        style={{ display:"none" }}
      />

      {/* Portalled overlays — render into <body> so nothing (including an
          overflow:hidden collapsed row) can clip them. Position is computed
          from the button's current bounding rect. */}
      {tipOpen && !menuOpen && !uploading && anchorRect && createPortal(
        <div style={{
          position:"fixed",
          top: Math.max(8, anchorRect.top - 8), // just above the button
          left: Math.min(window.innerWidth - 300, Math.max(8, anchorRect.right - 200)),
          transform: "translateY(-100%)",
          zIndex: 100000,
          background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
          padding:"8px 10px", fontSize:11, color:T.text,
          boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
          minWidth:200, maxWidth:300,
          pointerEvents:"none", fontFamily:FONT,
        }}>
          {tooltip}
        </div>,
        document.body
      )}

      {menuOpen && hasFile && anchorRect && createPortal(
        <div ref={menuRef} style={{
          position:"fixed",
          top: anchorRect.bottom + 4,
          left: Math.min(window.innerWidth - 200, Math.max(8, anchorRect.right - 200)),
          zIndex: 100000,
          background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
          boxShadow:"0 8px 24px rgba(0,0,0,0.5)", minWidth:200, padding:4,
          fontFamily:FONT,
        }}>
          <div style={{
            padding:"6px 10px 4px", fontSize:10, color:T.textMuted,
            borderBottom:`1px solid ${T.border}`, marginBottom:4,
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:240,
          }} title={attachment.file_name}>
            {attachment.file_name}
          </div>
          <MenuItem T={T} onClick={onView}    icon="👁" label="View in new tab" />
          {!isFallback && <MenuItem T={T} onClick={onReplace} icon="↻" label="Replace…" />}
          {!isFallback && <MenuItem T={T} onClick={onRemove}  icon="🗑" label="Remove" danger />}
          {isFallback && (
            <MenuItem T={T} onClick={() => { setMenuOpen(false); fileInputRef.current?.click(); }}
              icon="📎" label="Attach a different file to this row" />
          )}
        </div>,
        document.body
      )}

      {/* Transient error toast — also portalled */}
      {error && anchorRect && createPortal(
        <div style={{
          position:"fixed",
          top: anchorRect.bottom + 4,
          left: Math.max(8, anchorRect.right - 200),
          zIndex: 100001,
          background:T.red + "22", color:T.red, border:`1px solid ${T.red}55`,
          padding:"4px 8px", fontSize:10, borderRadius:RADIUS.sm,
          whiteSpace:"nowrap", fontFamily:FONT,
        }}>
          {error}
        </div>,
        document.body
      )}
    </span>
  );
}

function MenuItem({ T, onClick, icon, label, danger = false }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center", gap:8, width:"100%",
        background: hov ? T.surface : "transparent",
        border:"none", borderRadius:RADIUS.sm,
        color: danger ? T.red : T.text, fontSize:12, padding:"6px 10px",
        cursor:"pointer", fontFamily:FONT, textAlign:"left",
      }}>
      <span style={{ fontSize:12, width:14, textAlign:"center" }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}
